// Legend model + shared symbology for the project-scoped "Site Data" overlays (Potholes +
// Field Notes). These render client-side as GeoJSON (see map/PotholesLayer.tsx +
// map/FieldNotesLayer.tsx) with no `tilesConfig` entry, so — like the custom-data path
// (customDataLayers.helpers.ts) — the legend needs a bespoke section built here.
//
// The dot COLORS below are the single source of truth for both the map paint and the legend
// swatch, so the two can never drift (mirrors how `colorForCateTitle` is shared for
// custom-data lines). Field-note dots are Mapbox `circle` paint (fieldNotes/index.tsx); pothole
// dots are `GeoItemMarkerDot` markers (potholing/PotholeMapLayers.tsx).

import type { GeoItem } from '~/common/types';
import type { FieldNote } from '../fieldNotes/store';

/** Pothole / manhole marker: yellow dot, lightgreen ring (the non-High-priority default). */
export const POTHOLE_FILL = 'yellow';
export const POTHOLE_STROKE = 'lightgreen';

/** Field-note dots: notes are white-filled; photos/videos amber. All ringed orange. */
export const FIELD_NOTE_NOTE_FILL = 'white';
export const FIELD_NOTE_MEDIA_FILL = 'yellow';
export const FIELD_NOTE_STROKE = 'orange';

/** One dot row in the Site Data legend section. */
export interface SiteDataLegendItem {
  /** Stable key for React + `data-testid` (e.g. 'potholes', 'field-notes-media'). */
  key: string;
  label: string;
  fill: string;
  stroke: string;
}

/** The single "Site Data" legend group; `null` upstream when it has no rows. */
export interface SiteDataLegendSection {
  label: 'Site Data';
  items: SiteDataLegendItem[];
}

interface BuildArgs {
  /** Potholes layer is on + in scope (mirror of `shouldRenderSiteDataLayer`). */
  potholesActive: boolean;
  /** Geo-items already filtered to the org's report type. */
  potholeItems: readonly GeoItem[];
  /** Org `mobileReportType` — 'manholeInspection' relabels the row to "Manholes". */
  reportType?: string;
  /** Field Notes layer is on + in scope. */
  fieldNotesActive: boolean;
  fieldNotes: readonly FieldNote[];
}

/**
 * Pure legend model for the Site Data overlays. Emits a row only when that feature type is
 * actually present on the map: a Potholes/Manholes row when the layer is active and ≥1
 * report-type item exists; a "Photos & Videos" row when ≥1 image/video note exists; a "Notes"
 * row when ≥1 note exists. Returns `null` when no rows apply (so the caller can skip the
 * header entirely), matching "add these to the legend when they appear".
 */
export function buildSiteDataLegendSection({
  potholesActive,
  potholeItems,
  reportType,
  fieldNotesActive,
  fieldNotes,
}: BuildArgs): SiteDataLegendSection | null {
  const items: SiteDataLegendItem[] = [];

  if (potholesActive && potholeItems.length > 0) {
    items.push({
      key: 'potholes',
      label: reportType === 'manholeInspection' ? 'Manholes' : 'Potholes',
      fill: POTHOLE_FILL,
      stroke: POTHOLE_STROKE,
    });
  }

  if (fieldNotesActive) {
    const hasMedia = fieldNotes.some((n) => n.type === 'image' || n.type === 'video');
    const hasNotes = fieldNotes.some((n) => n.type === 'note');

    if (hasMedia) {
      items.push({
        key: 'field-notes-media',
        label: 'Photos & Videos',
        fill: FIELD_NOTE_MEDIA_FILL,
        stroke: FIELD_NOTE_STROKE,
      });
    }
    if (hasNotes) {
      items.push({
        key: 'field-notes-note',
        label: 'Notes',
        fill: FIELD_NOTE_NOTE_FILL,
        stroke: FIELD_NOTE_STROKE,
      });
    }
  }

  return items.length > 0 ? { label: 'Site Data', items } : null;
}
